import { useTranslation } from "react-i18next";

import { useMountEffect } from "@/hooks/useMountEffect";
import { syncDocumentLanguage } from "@/i18n/config";

const SITE_URL = "https://graphless.ai";
const OG_IMAGE = `${SITE_URL}/og-image.png`;

function setMetaProperty(property: string, content: string): void {
	let el = document.querySelector<HTMLMetaElement>(`meta[property="${property}"]`);
	if (!el) {
		el = document.createElement("meta");
		el.setAttribute("property", property);
		document.head.appendChild(el);
	}
	el.setAttribute("content", content);
}

function setMetaName(name: string, content: string): void {
	let el = document.querySelector<HTMLMetaElement>(`meta[name="${name}"]`);
	if (!el) {
		el = document.createElement("meta");
		el.setAttribute("name", name);
		document.head.appendChild(el);
	}
	el.setAttribute("content", content);
}

function setLink(rel: string, href: string, hreflang?: string): void {
	const selector = hreflang
		? `link[rel="${rel}"][hreflang="${hreflang}"]`
		: `link[rel="${rel}"]:not([hreflang])`;
	let el = document.querySelector<HTMLLinkElement>(selector);
	if (!el) {
		el = document.createElement("link");
		el.setAttribute("rel", rel);
		if (hreflang) el.setAttribute("hreflang", hreflang);
		document.head.appendChild(el);
	}
	el.setAttribute("href", href);
}

export function useDocumentMeta(): void {
	const { t, i18n } = useTranslation();

	useMountEffect(() => {
		const apply = (): void => {
			const lang = i18n.resolvedLanguage ?? i18n.language ?? "en";
			const ogLocale = lang.startsWith("pt") ? "pt_BR" : "en_US";
			const altLocale = lang.startsWith("pt") ? "en_US" : "pt_BR";

			document.title = t("meta.title");
			setMetaName("description", t("meta.description"));
			setMetaProperty("og:title", t("meta.ogTitle"));
			setMetaProperty("og:description", t("meta.ogDescription"));
			setMetaProperty("og:url", SITE_URL);
			setMetaProperty("og:image", OG_IMAGE);
			setMetaProperty("og:locale", ogLocale);
			setMetaProperty("og:locale:alternate", altLocale);
			setMetaName("twitter:title", t("meta.ogTitle"));
			setMetaName("twitter:description", t("meta.ogDescription"));
			setMetaName("twitter:image", OG_IMAGE);
			setLink("canonical", SITE_URL);
			setLink("alternate", SITE_URL, "en");
			setLink("alternate", SITE_URL, "pt");
			syncDocumentLanguage(lang);
		};

		apply();
		i18n.on("languageChanged", apply);
		return () => {
			i18n.off("languageChanged", apply);
		};
	});
}
